import { Link } from 'react-router-dom';
import LegalLayout from '@/components/LegalLayout';
import { BUSINESS } from '@/lib/legal';

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      intro={`This policy explains what information ${BUSINESS.brand} collects, how we use it, and the choices you have. It applies to our website and our mobile app.`}
    >
      <h2>Who we are</h2>
      <p>
        {BUSINESS.legalName} ("we", "us", "our") operates a prepaid tiffin (meal) subscription service in{' '}
        {BUSINESS.city}. You can reach us any time at{' '}
        <a href={`mailto:${BUSINESS.supportEmail}`}>{BUSINESS.supportEmail}</a>.
      </p>

      <h2>Information we collect</h2>
      <p>We only collect what we need to run your account and serve your tiffins:</p>
      <ul>
        <li><strong>Account details</strong> — your name, email address, phone number, and a password. Your password is stored only as a securely hashed value; we never see or store it in plain text.</li>
        <li><strong>Subscription &amp; order activity</strong> — the plans and add-ons you buy, your remaining tiffin balance, top-ups, discount codes you apply, and your pickup/redemption history.</li>
        <li><strong>Payment records</strong> — the amount, date, method, and status of each payment, plus the reference IDs returned by our payment processor. See "Payments &amp; card details" below.</li>
        <li><strong>Push-notification tokens</strong> — if you turn on notifications in the mobile app, we store a device push token so we can send you updates (e.g. when your subscription is running low). This is created only on the mobile app, never on the website.</li>
        <li><strong>Support &amp; account notes</strong> — messages you send us and notes our staff may add to help serve you.</li>
      </ul>

      <h2>Payments &amp; card details</h2>
      <p>
        Card payments (and Apple Pay where available) are processed securely by <strong>Square</strong>. Your
        full card number, expiry, and security code are entered into Square's secure form and sent directly to
        Square — <strong>{BUSINESS.brand} never receives or stores your card details</strong>. We only keep
        Square's payment and refund reference IDs so we can match a payment to your order and handle refunds or
        disputes. Square's handling of your card data is governed by Square's own privacy policy.
      </p>

      <h2>How we use your information</h2>
      <ul>
        <li>To create and manage your account and QR pickup pass.</li>
        <li>To process payments, activate plans, and track your tiffin balance.</li>
        <li>To send you service notifications and respond to support requests.</li>
        <li>To prevent fraud and misuse and to meet legal, tax, and accounting obligations.</li>
      </ul>

      <h2>When we share information</h2>
      <p>We do not sell your personal information. We share it only with:</p>
      <ul>
        <li><strong>Square</strong>, to process your payments.</li>
        <li><strong>Apple / Google (and Firebase)</strong>, to deliver push notifications you have opted into.</li>
        <li>Authorities or advisors where required by law.</li>
      </ul>

      <h2>How long we keep it</h2>
      <p>
        We keep your information while your account is active. When you delete your account we permanently remove
        your personal identifiers (name, email, phone), delete your password and push tokens, and deactivate the
        account. We retain the underlying payment/transaction records in <strong>anonymized</strong> form for as
        long as required for accounting, tax, and payment-dispute purposes.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>View and update your name and phone number any time in <strong>Profile</strong>.</li>
        <li>Turn push notifications on or off in your device settings.</li>
        <li>
          Delete your account and personal data — see our{' '}
          <Link to="/delete-account">Delete My Account</Link> page.
        </li>
      </ul>

      <h2>Security</h2>
      <p>Passwords are hashed, data is transmitted over encrypted connections (HTTPS), and access is limited to authorized staff.</p>

      <h2>Children</h2>
      <p>Our service is intended for adults and is not directed to children.</p>

      <h2>Changes to this policy</h2>
      <p>We may update this policy from time to time. We will post the new version here with an updated date.</p>

      <h2>Contact us</h2>
      <p>
        Questions about your privacy? Email <a href={`mailto:${BUSINESS.supportEmail}`}>{BUSINESS.supportEmail}</a>{' '}
        or call {BUSINESS.phone}.
      </p>
    </LegalLayout>
  );
}
